import { useEffect, useMemo, useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

// components
import { GenericTableHeader } from "@surgia/ui";
import TableControls from "@surgia/ui/containers/table/table-controls";

// Import the existing UsersTable component and FiltersModal from hub-surgia
import UsersTable from "../../../../../hub-surgia/src/containers/tables/users-table";
import { FiltersModal } from "../../../../../hub-surgia/src/pages/Users/index";

// queries
import { usersQueries } from "@surgia/api/src/queryStore/shared/users";
import config from "@surgia/config";
import { branchQueries } from "@surgia/api/src/queryStore/shared/branch";

// routes
import { ADMIN_PROTECTED_ROUTES } from "../../../routes";

// constants
const ITEMS_PER_PAGE = 8;

// Table Header Component

// Main Users Tab Component
function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: tenantId } = useParams<{ id: string }>();
  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("");
  const [search, setSearch] = useState("");
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [filters, setFilters] = useState({ city_id: "", user_group_id: "" });

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "userId", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  const statusTabs = [
    { label: "All", value: "" },
    { label: t("shared.tableToolbar.active"), value: "ACTIVE" },
    { label: t("shared.tableToolbar.pending"), value: "PENDING" },
  ];

  // Fetch branches for the dropdown using the proper query
  const { data: branchesResponse, isLoading: isBranchesLoading } = useQuery({
    ...branchQueries.queries.getBranchesForDropdown({
      tenant_id: parseInt(tenantId!),
      component_id: config.componentId,
    }),
  });

  // Add the userGroups query
  const { data: userGroupsResponse, isLoading: isUserGroupsLoading } = useQuery(
    {
      ...usersQueries.queries.getUserGroups({
        component_id: config.componentId,
        tenant_id: parseInt(tenantId!),
        target_component_id: config.hubComponentId,
        target_tenant_id: parseInt(tenantId!),
      }),
    }
  );

  // Extract userGroups from the response
  const userGroups = userGroupsResponse?.data || [];

  // Fetch users data for this specific tenant with selected branches and filters
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    ...usersQueries.queries.getUsers({
      component_id: config.hubComponentId,
      name: search,
      branches_ids: selectedBranch || "",
      id: "",
      profile_status: status !== "" && status ? status : "",
      size: ITEMS_PER_PAGE,
      page: currentPage,
      tenant_id: parseInt(tenantId!),
      city_id: filters.city_id || "",
      userGroup_id: filters.user_group_id || "",
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    }),
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        total: data.total,
        data: data.data.map((user: any) => ({
          ...user,
          id: user.user_id,
          name:
            user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : "_______",
          details: {
            email: user.email,
            phone: user.mobile,
          },
          role: user.user_groups,
          status: user.status,
          createdAt: user.creation_date,
          lastLogin: user?.last_login,
          branches: user.user_branches,
          actions: user.user_id,
        })),
      };
    },
  });

  // Computed values
  const totalCount = useMemo(() => {
    return usersResponse?.total || 0;
  }, [usersResponse?.total]);

  // Get branches from the API response instead of localStorage
  const branches = branchesResponse?.data || [];

  useEffect(() => {
    setCurrentPage(0);
  }, [search, selectedBranch, filters?.city_id, filters?.user_group_id]); // Reset page when filters change

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Handle view user details
  const handleViewUser = (userId: string) => {
    navigate(
      ADMIN_PROTECTED_ROUTES.TENANT_DETAILS_USER_DETAILS.replace(
        ":tenantId",
        tenantId!
      ).replace(":userId", userId)
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
      {/* Header with Branch Selector */}
      <GenericTableHeader
        title="Users"
        totalCount={totalCount}
        status={status}
        singularLabel="user"
        pluralLabel="users"
        layout="justified"
        borderStyle="gray-200"
        showBranchSelector={true}
        branches={branches}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        isBranchesLoading={isBranchesLoading}
        showAllBranches={true}
        branchPlaceholder="All Branches"
      />

      <TableControls
        status={status}
        setStatus={setStatus}
        search={search}
        setSearch={setSearch}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        statusTabs={statusTabs}
        showFilter={true}
      />

      {/* Users Table Section - Using the existing UsersTable component */}
      <UsersTable
        data={usersResponse?.data || []}
        currentPage={currentPage}
        totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
        onPageChange={setCurrentPage}
        loading={isUsersLoading}
        showSelectedBranchColumn={true}
        sortState={sortState}
        onSort={handleSort}
        onViewUser={handleViewUser}
      />

      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          branches={[]}
          userGroups={userGroups}
          isUserGroupsLoading={isUserGroupsLoading}
          showCities={false}
        />
      )}
    </div>
  );
}

export default Users;
